/**
 * Eén oordeel over een businesscase, uit de twee getallen die iets anders meten.
 *
 * Gemeten op 8 september 2026 (docs/onderzoek/businesscase-tegenspraak.md): drie
 * klantcases droegen op hetzelfde scherm twee tegengestelde oordelen. De gauge
 * drukte "Uitstekende ROI - Zeer aantrekkelijke investering" af op uitsluitend
 * `roiYear1`, terwijl de risicokaart twee kaarten verderop "Hoog Risico" toonde
 * omdat `totalCashflow` negatief is. JWM 30,26% bij -10.377 euro, Jeroen B
 * 24,54% bij -2.147, ArieB 2025 19,89% bij -15.303.
 *
 * Er zit geen rekenfout in. `roiYear1` is de jaar-1 omzet gedeeld door de netto
 * investering (businesscaseCalculations.ts:124-136; in de bruto-modus, waar alle
 * 20 niet-gearchiveerde cases op staan, zit de opex er niet in). `totalCashflow`
 * is omzet minus opex minus investering over de hele looptijd (regel 121). Jaar 1
 * tegenover tien à elf jaar: allebei correct, en samen misleidend.
 *
 * Daarom oordeelt geen enkel scherm meer op één getal. Deze module is de enige
 * plek waar een oordeel ontstaat; de componenten renderen het alleen nog. Twee
 * tegengestelde oordelen op één pagina zijn daarmee niet meer mogelijk zonder
 * deze functie zelf tegen te spreken — en dat is precies wat
 * tests/businesscase/oordeel.node.test.mjs toetst.
 *
 * Geen React, geen DOM, geen imports: bewust ook bruikbaar buiten de browser.
 * scripts/check-uitkomsten.mjs (toetsBusinesscase) doet dezelfde toets achteraf
 * op opgeslagen records; wie die twee ooit wil samenvoegen, verhuist dit bestand
 * naar src/lib/ en importeert het daar. Dat is op 8 september 2026 gebeurd,
 * omdat businesscasePDF.ts (ook in src/lib/) dit oordeel nodig had en lib/ niet
 * uit components/ hoort te importeren.
 */

/** Van sterk naar zwak. `onbekend` is geen oordeel maar de weigering er een te geven. */
export type OordeelNiveau = 'goed' | 'aandacht' | 'slecht' | 'onbekend';

/** Machineleesbare reden, zodat een test op de reden kan toetsen en niet op de tekst. */
export type OordeelReden =
  | 'kasstroom-negatief'
  | 'verdient-zich-niet-terug'
  | 'terugverdientijd-boven-10'
  | 'rendement-jaar1-onder-5'
  | 'terugverdientijd-boven-7'
  | 'rendement-jaar1-onder-10'
  | 'getallen-ontbreken';

export interface OordeelInvoer {
  /** Rendement over UITSLUITEND het eerste jaar, in procenten. */
  roiYear1?: number | null;
  /** Omzet minus opex minus netto investering over de hele looptijd, in euro. */
  totalCashflow?: number | null;
  /**
   * Terugverdientijd in jaren. `null` betekent nadrukkelijk "verdient zich niet
   * terug binnen de looptijd" — het tegenovergestelde van een korte tijd.
   * `undefined` betekent "niet meegegeven": dan blijft de terugverdientijd
   * buiten het oordeel in plaats van als nul te tellen.
   */
  paybackPeriod?: number | null;
  roiMode?: 'bruto' | 'netto';
  /** Aantal jaarrijen; alleen voor de uitlegtekst, niet voor het oordeel. */
  looptijdJaren?: number;
}

export interface Oordeel {
  niveau: OordeelNiveau;
  /** Waar alleen `niveau === 'goed'` positief is. Eén bron voor elke groene tekst. */
  positief: boolean;
  /** Korte kop: het oordeel zelf. */
  kop: string;
  /** Het label van de risicokaart; ongewijzigd t.o.v. de oude assessRisk. */
  risicoLabel: string;
  /** Volledige zin, met beide getallen en hun periode erin. */
  uitleg: string;
  redenen: OordeelReden[];
}

/**
 * De drempels stonden vóór deze wijziging in RiskIndicator.tsx:23 en :32 en zijn
 * daar ongewijzigd overgenomen — dit is een presentatiereparatie, geen nieuwe
 * norm. De enige toevoeging is dat een ontbrekende terugverdientijd
 * (`paybackPeriod === null`) nu zelfstandig zwaar telt; de PDF deed dat al
 * (businesscasePDF.ts:435-443 eist `payback !== null` voor "Laag" of "Matig").
 */
export const OORDEEL_DREMPELS = {
  rendementJaar1Onder: 5,
  rendementJaar1Matig: 10,
  terugverdientijdBoven: 7,
  terugverdientijdTeLang: 10,
};

function alsGetal(waarde: number | null | undefined): number | null {
  return typeof waarde === 'number' && Number.isFinite(waarde) ? waarde : null;
}

function procent(waarde: number): string {
  return `${waarde.toFixed(1)}%`;
}

function euro(waarde: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(waarde);
}

/**
 * Het label bij een ROI-getal, mét de periode erin. "ROI 25%" zonder jaartal is
 * de kern van de verwarring: het percentage geldt voor het eerste jaar en nodigt
 * uit tot doortrekken, terwijl de opbrengst in alle drie de gemeten cases met
 * 43 tot 47 procent daalt over de looptijd.
 */
export function roiPeriodeLabel(roiMode?: 'bruto' | 'netto'): string {
  if (roiMode === 'netto') return 'ROI jaar 1 (netto)';
  if (roiMode === 'bruto') return 'ROI jaar 1 (bruto)';
  return 'ROI jaar 1';
}

/** Zin die het jaar-1-getal aan de looptijduitkomst koppelt. */
function looptijdZin(kasstroom: number, looptijdJaren?: number): string {
  const periode =
    typeof looptijdJaren === 'number' && looptijdJaren > 0
      ? `over de looptijd (${looptijdJaren} jaar)`
      : 'over de hele looptijd';
  return kasstroom < 0
    ? `${periode} komt er ${euro(Math.abs(kasstroom))} minder binnen dan eruit ging`
    : `${periode} blijft er ${euro(kasstroom)} over`;
}

export function beoordeelBusinesscase(invoer: OordeelInvoer): Oordeel {
  const roi = alsGetal(invoer.roiYear1);
  const kasstroom = alsGetal(invoer.totalCashflow);
  const terugverdientijdOnbekend = invoer.paybackPeriod === undefined;
  const terugverdientijd = alsGetal(invoer.paybackPeriod);
  const geenTerugverdientijd = !terugverdientijdOnbekend && terugverdientijd === null;

  // Niets weten is geen oordeel. Een scherm dat de getallen niet heeft, hoort dat
  // te zeggen in plaats van stilzwijgend groen of rood te kleuren — dat stille
  // meemelden is precies wat de vier records met total_revenues = 0 verborg.
  if (roi === null || kasstroom === null) {
    return {
      niveau: 'onbekend',
      positief: false,
      kop: 'Nog geen oordeel',
      risicoLabel: 'Nog geen oordeel',
      uitleg:
        'Rendement of kasstroom ontbreekt. Zolang beide er niet zijn, is er geen oordeel over deze businesscase te geven — vul de jaarrijen aan.',
      redenen: ['getallen-ontbreken'],
    };
  }

  const zwaar: OordeelReden[] = [];
  const matig: OordeelReden[] = [];

  if (kasstroom < 0) zwaar.push('kasstroom-negatief');
  if (geenTerugverdientijd) zwaar.push('verdient-zich-niet-terug');
  if (terugverdientijd !== null && terugverdientijd > OORDEEL_DREMPELS.terugverdientijdTeLang) {
    zwaar.push('terugverdientijd-boven-10');
  }
  if (roi < OORDEEL_DREMPELS.rendementJaar1Onder) zwaar.push('rendement-jaar1-onder-5');

  if (roi >= OORDEEL_DREMPELS.rendementJaar1Onder && roi < OORDEEL_DREMPELS.rendementJaar1Matig) {
    matig.push('rendement-jaar1-onder-10');
  }
  if (
    terugverdientijd !== null &&
    terugverdientijd > OORDEEL_DREMPELS.terugverdientijdBoven &&
    terugverdientijd <= OORDEEL_DREMPELS.terugverdientijdTeLang
  ) {
    matig.push('terugverdientijd-boven-7');
  }

  const rendementZin = `${roiPeriodeLabel(invoer.roiMode)} is ${procent(roi)}`;

  if (zwaar.length > 0) {
    const kop = zwaar.includes('kasstroom-negatief')
      ? 'Verliesgevend over de looptijd'
      : zwaar.includes('verdient-zich-niet-terug')
        ? 'Verdient zich niet terug'
        : zwaar.includes('terugverdientijd-boven-10')
          ? 'Terugverdientijd langer dan tien jaar'
          : 'Rendement in jaar 1 te laag';

    const staart =
      kasstroom < 0
        ? `, maar ${looptijdZin(kasstroom, invoer.looptijdJaren)}. Het jaar-1-percentage geldt alleen voor het eerste jaar en mag niet worden doorgetrokken.`
        : geenTerugverdientijd
          ? ` en ${looptijdZin(kasstroom, invoer.looptijdJaren)}, maar de investering verdient zich niet terug binnen de looptijd.`
          : ` en ${looptijdZin(kasstroom, invoer.looptijdJaren)}, met een terugverdientijd van ${terugverdientijd} jaar.`;

    return {
      niveau: 'slecht',
      positief: false,
      kop,
      risicoLabel: 'Hoog Risico',
      uitleg: `${rendementZin}${staart}`,
      redenen: zwaar.concat(matig),
    };
  }

  if (matig.length > 0) {
    return {
      niveau: 'aandacht',
      positief: false,
      kop: 'Haalbaar, met kanttekeningen',
      risicoLabel: 'Matig Risico',
      uitleg:
        `${rendementZin} en ${looptijdZin(kasstroom, invoer.looptijdJaren)}` +
        (terugverdientijd !== null ? `, terugverdientijd ${terugverdientijd} jaar.` : '.') +
        ' Acceptabel, maar niet optimaal.',
      redenen: matig,
    };
  }

  return {
    niveau: 'goed',
    positief: true,
    kop: 'Sluitende businesscase',
    risicoLabel: 'Laag Risico',
    uitleg:
      `${rendementZin} en ${looptijdZin(kasstroom, invoer.looptijdJaren)}` +
      (terugverdientijd !== null ? `, terugverdientijd ${terugverdientijd} jaar.` : '.'),
    redenen: [],
  };
}
